import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import {
  useGetStockLevelsQuery,
  useAdjustStockMutation,
  useGetLocationsQuery,
  useCreateLocationMutation,
} from '../../../store/api/inventoryApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function InventoryDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');

  const { data: stockLevels, refetch } = useGetStockLevelsQuery({
    locationId: selectedLocation || undefined,
    lowStock: lowStockOnly || undefined,
  });
  const { data: locations } = useGetLocationsQuery();
  const { data: productsData } = useGetProductsQuery({ limit: 1000 });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const [adjustStock] = useAdjustStockMutation();
  const [createLocation] = useCreateLocationMutation();

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const handleAdjustStock = async () => {
    try {
      await adjustStock({
        productId: selectedProduct.id,
        locationId: selectedProduct.locationId,
        quantity: parseFloat(adjustQuantity),
        notes: adjustNotes,
      }).unwrap();
      refetch();
      setAdjustDialogOpen(false);
      setSelectedProduct(null);
      setAdjustQuantity('');
      setAdjustNotes('');
    } catch (error) {
      alert(error?.data?.message || 'Error adjusting stock');
    }
  };

  const filteredStock = stockLevels?.filter((stock) => {
    if (searchTerm) {
      const product = productsData?.products?.find((p) => p.id === stock.productId);
      return (
        product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product?.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Inventory Management
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Search Products"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            select
            label="Location"
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
          >
            <MenuItem value="">All Locations</MenuItem>
            {locations?.map((location) => (
              <MenuItem key={location.id} value={location.id}>
                {location.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setLowStockOnly(!lowStockOnly)}
            startIcon={<WarningIcon />}
            color={lowStockOnly ? 'warning' : 'primary'}
          >
            {lowStockOnly ? 'Show All' : 'Low Stock Only'}
          </Button>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reserved</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Available</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Avg Cost (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Stock Value (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reorder Point</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStock?.length > 0 ? (
                  filteredStock.map((stock) => {
                    const product = productsData?.products?.find((p) => p.id === stock.productId);
                    const available = parseFloat(stock.quantity) - parseFloat(stock.reservedQuantity);
                    const isLowStock = available <= parseFloat(stock.reorderPoint);
                    return (
                      <TableRow key={stock.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {product?.name || 'Unknown'}
                        </TableCell>
                        <TableCell>{product?.sku || '-'}</TableCell>
                        <TableCell>{stock.location?.name || '-'}</TableCell>
                        <TableCell>{stock.quantity}</TableCell>
                        <TableCell>{stock.reservedQuantity}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: isLowStock ? 'error.main' : 'success.main' }}>
                          {available}
                        </TableCell>
                        <TableCell>{formatAmount(stock.fifoAvgCost)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatAmount(stock.fifoValue)}</TableCell>
                        <TableCell>{stock.reorderPoint}</TableCell>
                        <TableCell>
                          {isLowStock ? (
                            <Chip label="Low Stock" color="warning" size="small" />
                          ) : (
                            <Chip label="In Stock" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedProduct({
                                id: stock.productId,
                                locationId: stock.locationId,
                                name: product?.name,
                              });
                              setAdjustDialogOpen(true);
                            }}
                          >
                            Adjust
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No stock levels found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Stock Adjustment Dialog */}
      <Dialog open={adjustDialogOpen} onClose={() => setAdjustDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Stock</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Product: {selectedProduct?.name}
            </Typography>
            <TextField
              fullWidth
              label="Adjustment Quantity"
              type="number"
              value={adjustQuantity}
              onChange={(e) => setAdjustQuantity(e.target.value)}
              helperText="Positive for increase, negative for decrease"
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={adjustNotes}
              onChange={(e) => setAdjustNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAdjustStock} variant="contained">
            Adjust Stock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default InventoryDashboard;
